//? SHARE with navigator.share
import React, {useEffect, useRef, useState} from "react";
import {t} from "i18next";
import pdfMake from "pdfmake/build/pdfmake";

import {instance} from "../../../../api/apiSetup";
import {apiUrl} from "../../../../api/baseURL";
import FunctionButton from "../../../../utilities/Buttons/FunctionButton";
import IconButton from "../../../../utilities/Buttons/IconButton";
import {ICON_NAMES} from "../../../../utilities/generalConstants.js/generalConstants";
import {toaster} from "../../../../utilities/hooks/toaster";
import {UI_PERMISSION_TYPE} from "../../../adminForms/Ba3aSettings/tabs/PositionsSettings/useRoutePermissions";
import {useModal} from "../../../Modal/ModalProvider";
import NewPdfViewer from "../../../Project/Documents/DocumentView/NewPdfViewer";
import Spinner from "../../../spinner/Spinner";

import "./PdfPrevievModal.scss";
const PdfPreviewModal = ({
  pdfFile,
  fileArray,
  exportToPdf,
  noBtns = false,
  printBtnEnable = false,
  linkBtnEnable = false,
  shareLink,
  fileName: fileNameRow,
  entityId,
  entityName,
}) => {
  const fileName = preparePdfFileName(fileNameRow);
  const {closeModal} = useModal();
  const [isPreparing, setIsPreparing] = useState(false);
  const [blobObjects, setBlobObjects] = useState(null);
  const previewReqIdRef = useRef(0);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    setBlobObjects(null);
  }, [entityName, entityId]);

  useEffect(() => {
    const reqId = ++previewReqIdRef.current;
    const parent = document.getElementById("pdfContainer");
    if (parent) parent.innerHTML = "";

    let objectUrl = null;

    if (pdfFile) {
      const renderIframe = src => {
        if (reqId !== previewReqIdRef.current) return;
        const iframe = document.createElement("iframe");
        const parent = document.getElementById("pdfContainer");
        if (!parent) return;
        iframe.src = src;
        iframe.classList.add("pdf-iframe");
        parent.appendChild(iframe);
      };

      if (typeof pdfFile.getBlob === "function") {
        setLoading(true);
        pdfFile.getBlob(blob => {
          if (reqId !== previewReqIdRef.current) return;
          objectUrl = URL.createObjectURL(blob);
          renderIframe(objectUrl);
          setLoading(false);
        });
      } else if (typeof pdfFile.getDataUrl === "function") {
        setLoading(true);
        pdfFile.getDataUrl(dataUrl => {
          renderIframe(dataUrl);
          setLoading(false);
        });
      }
    }

    return () => {
      const parent = document.getElementById("pdfContainer");
      if (parent) parent.innerHTML = "";

      if (objectUrl) {
        URL.revokeObjectURL(objectUrl);
        objectUrl = null;
      }
    };
  }, [pdfFile]);

  const links = prepareLinks({entityId, entityName});
  const ensureBlobsReady = async () => {
    if (!linkBtnEnable) {
      throw new Error("Sharing disabled");
    }
    if (blobObjects) return blobObjects;

    if (!links?.post || !links?.get) {
      throw new Error(t("Share functionality is not available for this document"));
    }

    setIsPreparing(true);
    try {
      const {data} = await instance.post(links.post);
      const shareIds = (Array.isArray(data) ? data : [data]).filter(Boolean);

      if (!shareIds.length) {
        throw new Error("No share links were returned");
      }

      const blobs = await Promise.all(
        shareIds.map(async shareId => {
          const {data: blob} = await instance.get(`${links.get}/${shareId}`, {
            responseType: "blob",
          });

          return blob;
        }),
      );

      setBlobObjects(blobs);
      return blobs;
    } catch (err) {
      console.error(err);
      toaster.error(t("File preparation error"));
      throw err;
    } finally {
      setIsPreparing(false);
    }
  };

  const getFileNameFromUrl = url => {
    if (!url) return fileName || "document.pdf";
    const parts = url.split("?")[0].split("/");
    return decodeURIComponent(parts[parts.length - 1]);
  };

  const getSharedFileName = (index, filesCount) => {
    if (fileArray?.[index]) return getFileNameFromUrl(fileArray[index]);
    if (filesCount === 1) return fileName;

    return fileName.replace(/\.pdf$/i, `_${index + 1}.pdf`);
  };

  const getPdfBlob = docDefinition => {
    if (!docDefinition) return;
    return new Promise(resolve => {
      pdfMake.createPdf(docDefinition).getBlob(blob => {
        resolve(blob);
      });
    });
  };

  const pdfFileBlob = getPdfBlob(pdfFile);

  const handleShareClick = async () => {
    if (pdfFileBlob && !links) {
      try {
        const blob = await pdfFileBlob;
        const file = new File([blob], fileName, {
          type: blob.type || "application/pdf",
        });

        if (navigator.share && navigator.canShare && navigator.canShare({files: [file]})) {
          await navigator.share({files: [file]});
          return;
        }

        toaster.info(t("Web Share API is not supported for this platform"));
        const url = URL.createObjectURL(file);
        const a = document.createElement("a");
        a.href = url;
        a.download = file.name;
        a.click();
        URL.revokeObjectURL(url);
      } catch (error) {
        if (error.name === "AbortError") {
          toaster.info(t("Sharing was cancelled"));
        } else {
          console.error(error);
          toaster.error(t("Something went wrong while sharing..."));
        }
      }

      return;
    }

    if (shareLink) {
      const url = `${apiUrl}/${shareLink}`;

      if (navigator.share) {
        try {
          await navigator.share({
            title: fileName,
            text: "Here is your document",
            url,
          });
        } catch (err) {
          console.error(err);
        }
      } else {
        await navigator.clipboard.writeText(url);
        alert("Link copied");
      }

      return;
    }

    try {
      const blobs = await ensureBlobsReady();

      if (!blobs?.length) return;
      const files = blobs.map((blob, index) =>
        new File([blob], getSharedFileName(index, blobs.length), {
          type: blob.type || "application/pdf",
          lastModified: Date.now(),
        }),
      );

      if (navigator.share && navigator.canShare && navigator.canShare({files})) {
        try {
          await navigator.share({
            files,
            text: t("Document"),
          });
          toaster.success(t("Redirect successful"));
        } catch (err) {
          if (err.name === "AbortError") {
            toaster.info(t("Sharing was cancelled"));
          } else {
            console.error(err);
            toaster.error(t("Something went wrong while sharing..."));
          }
        }
      } else {
        toaster.info(t("Web Share API is not supported for this platform"));
        files.forEach(file => {
          const url = URL.createObjectURL(file);
          const a = document.createElement("a");
          a.href = url;
          a.download = file.name;
          a.click();
          URL.revokeObjectURL(url);
        });
      }
    } catch (err) {
      console.error(err);
      toaster.error(t("Something went wrong..."));
    }
  };

  return (
    <div className="pdf-preview w100 h100 fcCol fGrow">
      {pdfFile && (
        <div className="w100 fGrow" id="pdfContainer">
          {loading && (
            <div className="h100 w100 row jcc aic">
              <Spinner style={{position: "static"}} />
            </div>
          )}
        </div>
      )}
      {fileArray && (
        <>
          {loading && (
            <div className="center">
              <Spinner />
            </div>
          )}
          <NewPdfViewer fileArray={fileArray} hiding={false} printBtnEnable={printBtnEnable} className="fGrow" />
        </>
      )}
      {linkBtnEnable && (
        <div className="pdf-container">
          <IconButton
            nameIcon={ICON_NAMES.SHARE}
            onClick={handleShareClick}
            disabled={isPreparing}
            ariaLabel="Share"
            className="shareBtn"
            widthIcon={20}
            heightIcon={20}
            tooltipMessage="Share"
            uiPermissionType={UI_PERMISSION_TYPE.READABLE}
          />
        </div>
      )}
      {!noBtns && (
        <div className="fc w100 nowrap jcc g2 mt2x">
          <FunctionButton
            name={t("Save")}
            onClick={() => {
              closeModal();

              if ((!exportToPdf || typeof exportToPdf !== "function") && pdfFile) {
                pdfFile.download(`order_${fileName}`);

                return;
              }

              typeof exportToPdf === "function" && exportToPdf(false);
            }}
            autoWidth
            uiPermissionType={UI_PERMISSION_TYPE.READABLE}
          />
          <FunctionButton
            name={t("Save + pdf")}
            onClick={() => {
              closeModal();

              if ((!exportToPdf || typeof exportToPdf !== "function") && pdfFile) {
                pdfFile.download(`order_${fileName}`);
                pdfFile.print();
                return;
              }

              typeof exportToPdf === "function" && exportToPdf(true);
            }}
            autoWidth
            uiPermissionType={UI_PERMISSION_TYPE.READABLE}
          />
        </div>
      )}
    </div>
  );
};

export default PdfPreviewModal;

export const preparePdfFileName = (value, fallback = "document.pdf") => {
  if (typeof value !== "string" || !value.trim()) {
    return fallback;
  }
  let name = value
    .trim()
    .trim()
    .replace(/[<>:"/\\|?*]/g, "_")
    .replace(/\s+/g, " ");

  if (!name.toLowerCase().endsWith(".pdf")) {
    name += ".pdf";
  }

  return name;
};

export const prepareLinks = ({entityName, entityId}) => {
  if (!entityName || entityId === undefined || entityId === null) {
    console.error("enter entityName and entityId in prepareLinks");
    return;
  }

  return {
    post: `/share/link/${encodeURIComponent(entityName)}/${encodeURIComponent(entityId)}`,
    get: `/share/download`,
  };
};
